import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, ExternalLink, Loader2, Sparkles, MessageSquare, TrendingUp, Beaker } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const EMOJI = { 1: "😞", 2: "😕", 3: "😐", 4: "🙂", 5: "😄" };

export default function LabReviews() {
  const [data, setData] = useState({ reviews: [], stats: { count: 0, avg: 0, distribution: {} } });
  const [program, setProgram] = useState({ enabled: true, external_url: "", redirect_threshold: 4, thank_you_message: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/lab/reviews"),
        api.get("/lab/review-program"),
        api.get("/lab/reviews/trend?weeks=12"),
      ]);
      setData(r1.data);
      setProgram({ ...r2.data.effective, ...r2.data.override });
      setTrend(r3.data.trend || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load reviews"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/lab/review-program", program);
      toast.success("Review program updated");
      await load();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const stats = data.stats;
  const dist = stats.distribution || {};
  const maxCount = Math.max(1, ...Object.values(dist));

  return (
    <DashboardLayout role="lab_admin">
      <div className="max-w-6xl space-y-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink">Patient Reviews</h1>
          <p className="text-sm text-ink/60 mt-1">Only your <b>Laboratory rating</b> is shown here. EFGH platform experience ratings are aggregated by the super-admin and never mixed with your lab&apos;s score.</p>
        </div>

        {/* Program config */}
        <Card className="p-6 rounded-2xl" data-testid="review-program-card">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-efgh-yellow" />
            <div className="font-heading font-semibold text-lg text-ink">Review wizard settings</div>
          </div>
          <p className="text-sm text-ink/60 mt-1">Configure how patients rate their reports and where high ratings redirect.</p>

          <div className="mt-5 space-y-4">
            <label className="flex items-center gap-3 p-3 rounded-xl border border-border">
              <input
                type="checkbox"
                data-testid="review-enabled"
                checked={!!program.enabled}
                onChange={(e) => setProgram({ ...program, enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <div className="flex-1">
                <div className="font-medium text-ink text-sm">Enable review wizard on Smart Reports</div>
                <div className="text-xs text-ink/50">Shows an emoji rating dialog once per report after it&apos;s published.</div>
              </div>
            </label>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>External review URL</Label>
                <Input
                  data-testid="review-external-url"
                  placeholder="https://g.page/your-lab/review"
                  value={program.external_url || ""}
                  onChange={(e) => setProgram({ ...program, external_url: e.target.value })}
                />
                <div className="text-[11px] text-ink/50 mt-1">Google Business, JustDial, Practo — anywhere public. Blank = no redirect.</div>
              </div>
              <div>
                <Label>Redirect threshold (rating ≥)</Label>
                <div className="flex gap-1 mt-1" data-testid="threshold-picker">
                  {[3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setProgram({ ...program, redirect_threshold: n })}
                      data-testid={`threshold-${n}`}
                      className={`flex-1 h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1 transition-all ${
                        program.redirect_threshold === n ? "bg-ink text-white border-ink" : "border-border text-ink/70 hover:bg-muted"
                      }`}
                    >
                      {EMOJI[n]} {n}+
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-ink/50 mt-1">Ratings below this stay internal only.</div>
              </div>
            </div>

            <div>
              <Label>Thank-you message (shown after low ratings)</Label>
              <Input
                data-testid="review-thankyou"
                placeholder="Thank you! Your feedback helps us improve."
                value={program.thank_you_message || ""}
                onChange={(e) => setProgram({ ...program, thank_you_message: e.target.value })}
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-review-program" className="bg-ink hover:bg-ink/90 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save review program
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats — Laboratory rating ONLY */}
        <div className="grid md:grid-cols-3 gap-4" data-testid="lab-rating-stats">
          <Card className="p-5 rounded-2xl">
            <div className="text-[11px] uppercase tracking-widest text-ink/50">Total reviews</div>
            <div className="mt-2 font-heading font-bold text-3xl text-ink" data-testid="review-count">{stats.count || 0}</div>
          </Card>
          <Card className="p-5 rounded-2xl">
            <div className="text-[11px] uppercase tracking-widest text-ink/50 flex items-center gap-1"><Beaker className="w-3 h-3" /> Overall lab rating</div>
            <div className="mt-2 font-heading font-bold text-3xl text-ink flex items-center gap-2">
              <Star className="w-6 h-6 fill-efgh-yellow text-efgh-yellow" />
              <span data-testid="review-avg">{(stats.avg || 0).toFixed(2)}</span>
              <span className="text-base text-ink/40">/5</span>
            </div>
            <div className="mt-1 text-[11px] text-ink/50">Reflects patient experience with <b>your lab</b>.</div>
          </Card>
          <Card className="p-5 rounded-2xl">
            <div className="text-[11px] uppercase tracking-widest text-ink/50">Distribution</div>
            <div className="mt-2 space-y-1">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n} className="flex items-center gap-2 text-xs">
                  <span className="w-6">{EMOJI[n]}</span>
                  <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                    <div className="h-full bg-efgh-yellow" style={{ width: `${((dist[n] || 0) / maxCount) * 100}%` }} />
                  </div>
                  <span className="text-ink/60 w-6 text-right">{dist[n] || 0}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Review Trend — last 12 weeks */}
        <Card className="rounded-2xl p-6" data-testid="review-trend-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-efgh-green" />
              <div className="font-heading font-semibold text-lg text-ink">Review trend</div>
            </div>
            <div className="text-[11px] text-ink/50">Weekly average · last 12 weeks</div>
          </div>
          <div className="mt-3 h-52">
            {trend.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-ink/40">No trend data yet — trend will appear as patients submit ratings.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 10, right: 10, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={26} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => Number(v).toFixed(2)} />
                  <Line type="monotone" dataKey="avg" stroke="#F5C400" strokeWidth={2.5} dot={{ r: 3, fill: "#F5C400" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Review list */}
        <Card className="rounded-2xl">
          <div className="p-5 border-b border-border">
            <div className="font-heading font-semibold text-lg text-ink flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Recent reviews</div>
          </div>
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
          ) : (data.reviews || []).length === 0 ? (
            <div className="p-10 text-center text-ink/50 text-sm">No reviews yet. Patients will rate their reports here once you publish them.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.reviews.map((r) => (
                <div key={r.id} className="p-4 flex items-start gap-4" data-testid={`review-row-${r.id}`}>
                  <span className="text-3xl leading-none" title={`Rated ${r.rating}/5`}>{EMOJI[r.rating]}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink">{r.patient_name || r.patient_email || "Patient"}</div>
                    <div className="text-xs text-ink/50">{r.created_at?.slice(0, 10)}{r.redirected_external ? " · shared publicly" : ""}</div>
                    {r.feedback && <div className="mt-1 text-sm text-ink/80">{r.feedback}</div>}
                  </div>
                  {r.redirected_external && <span className="pill pill-normal text-[10px] flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Public</span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
